import type {
  ImageBaseRawResp,
  NewItemRawResp,
  OverrideBaseRawResp,
  SuspensionInfoRawResp,
} from './newMenuPage.response';
import type {
  CategoryId,
  CustomizationId,
  MenuId,
  ProductId,
  StoreId,
} from 'shared/domain';
import { IMenuCustomization } from 'features/menu-page/domain';

interface OverrideContext {
  storeId: StoreId;
  menuId: MenuId;
  categoryId: CategoryId;
  itemId: ProductId | CustomizationId | number;
}

interface ToTitleInfoParams extends OverrideContext {
  titleInfo?: NewItemRawResp['title_info'];
}

interface ToDescriptionInfoParams extends OverrideContext {
  descriptionInfo?: NewItemRawResp['description_info'];
}

interface ToImageInfoParams extends OverrideContext {
  imagesInfo?: NewItemRawResp['images_info'];
}

interface ToOutOfStockParams extends OverrideContext {
  suspensionInfo?: SuspensionInfoRawResp;
}
type ToOutOfStockReturn = Pick<
  IMenuCustomization,
  'isAvailable' | 'isOn' | 'suspensionInfo'
>;

interface FindMatchingOverrideParams<T> {
  overrides: T[];
  context: OverrideContext;
}

function findMatchingOverride<O extends OverrideBaseRawResp>(
  params: FindMatchingOverrideParams<O>,
): O | undefined {
  const { overrides, context } = params;
  for (const override of overrides) {
    const { override_type: oType, override_id: oId } = override;
    let hasMatch = false;
    if (oType === 'STORE') hasMatch = oId === context.storeId;
    else if (oType === 'MENU') hasMatch = oId === context.menuId;
    else if (oType === 'CATEGORY') hasMatch = oId === context.categoryId;
    else if (oType === 'ITEM') hasMatch = oId === context.itemId;
    if (hasMatch) return override;
  }
  return undefined;
}

export function toTitleInfo(params: ToTitleInfoParams) {
  const { titleInfo, ...context } = params;
  const matchingOverride = findMatchingOverride({
    overrides: titleInfo?.overrides ?? [],
    context,
  });
  return matchingOverride?.title ?? titleInfo?.title ?? '';
}

export function toDescriptionInfo(params: ToDescriptionInfoParams) {
  const { descriptionInfo, ...context } = params;
  const matchingOverride = findMatchingOverride({
    overrides: descriptionInfo?.overrides ?? [],
    context,
  });
  return matchingOverride?.description ?? descriptionInfo?.description ?? '';
}

export function toImagesInfo(params: ToImageInfoParams) {
  const { imagesInfo, ...context } = params;
  let matchingImg: ImageBaseRawResp | undefined = imagesInfo?.images[0];
  const matchingOverride = findMatchingOverride({
    overrides: imagesInfo?.overrides ?? [],
    context,
  });
  matchingImg = matchingOverride?.images?.[0] ?? matchingImg;
  return (matchingImg?.host ?? '') + (matchingImg?.path ?? '');
}

export function toOutOfStock(params: ToOutOfStockParams): ToOutOfStockReturn {
  const { suspensionInfo, ...context } = params;
  if (!suspensionInfo) {
    return {
      isAvailable: true,
      isOn: true,
      suspensionInfo: {
        reason: null,
        shutdownDate: null,
        activationDate: null,
      },
    };
  }
  if (!suspensionInfo.is_available) {
    return {
      isAvailable: false,
      isOn: false,
      suspensionInfo: {
        reason: suspensionInfo.suspend_reason,
        shutdownDate: null,
        activationDate: suspensionInfo.suspend_until,
      },
    };
  }
  const matchingOverride = findMatchingOverride({
    overrides: suspensionInfo.overrides,
    context,
  });
  return {
    isAvailable: true,
    isOn: matchingOverride === undefined,
    suspensionInfo: {
      reason: matchingOverride?.suspend_reason ?? null,
      shutdownDate: null,
      activationDate: matchingOverride?.suspend_until ?? null,
    },
  };
}
